import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import {useEffect, useState, useRef, useMemo, useCallback} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {useSnackbar} from "notistack";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {Box, Button, Typography} from "@mui/material";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import {CustomCellRendererProps} from "ag-grid-react";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import {
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    TextField,  
    Menu,
	MenuItem,
    Chip  
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {AddCircleOutline, Delete, Edit, MoreVert} from "@mui/icons-material";
import {DateTime} from 'luxon';
import {DateTimePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterLuxon} from '@mui/x-date-pickers/AdapterLuxon'
import { format } from "date-fns";
import {useDialogs} from "@toolpad/core";
import 'react-quill/dist/quill.snow.css';
import ReactQuill, { Quill } from 'react-quill';
import ImageResize from 'quill-image-resize-module-react';
import { GlobalStyles } from '@mui/material';

interface PageData {
    id: string
    title: string,
    description: string,
    created_at: string,
    is_draft: boolean;
}

interface PaginationData {
    data: PageData[];
    total_count: number;
    total_pages: number;
    current_page: number;
    page_size: number;
}


if (typeof window !== 'undefined') {
    (window as any).Quill = Quill;
}

Quill.register('modules/imageResize', ImageResize);

export default function AdminUpdate() {
    const { enqueueSnackbar } = useSnackbar();
    const dialogs = useDialogs();
    
    const [pageData, setPageData] = useState<PaginationData>();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [openAddUpdatePost, setOpenAddUpdatePost] = useState(false)
    const [editingPostData, setEditingPostData] = useState<any | null>(null);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [
            campaignCustomStartDatetime,
            setCampaignCustomStartDatetime
        ] = useState<DateTime | null>(null);

    const pageDataQuery = useQuery({
		queryKey: ["UpdatePostsPageData", currentPage, pageSize, searchQuery],
		queryFn: () => authenticateAndFetchData(`/admin/admin-get-update-posts/?page=${currentPage}&page_size=${pageSize}&search=${encodeURIComponent(searchQuery)}`),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	})
    
	useEffect(() => {
        if (pageDataQuery.data) {
            const paginationData = pageDataQuery.data?.data?.data;

            if (paginationData) {
            setPageData({
                data: paginationData.data || [],
                total_count: paginationData.total_count || 0,
                total_pages: paginationData.total_pages || 0,
                current_page: paginationData.current_page || 1,
                page_size: paginationData.page_size || 10,
            });
            }
        }
    }, [pageDataQuery.data]);

    const deleteUpdatePostMutation = useMutation({
		mutationKey: ["deleteUpdatePost"],
		mutationFn: (ids: number[]) => authenticateAndPostData("/admin/admin-delete-udpate-post/", {
			updatePostIds: ids,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
            pageDataQuery.refetch().then();
            enqueueSnackbar("Successfully Deleted the update post.", {
				variant: "success",
			});
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const handleSearchChange = (search: string) => {
        setSearchQuery(search);
        setCurrentPage(1); // Reset to first page when searching
    };

    function timestampFormatter(params: ValueFormatterParams) {            
            if (params.value !== null) {
                return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
            } else {
                return "---"
            }
        }

	function handleDeleteSelected(ids: number[]) {
        deleteUpdatePostMutation.mutate(ids);
    }

    const ActionsButton = (props: CustomCellRendererProps) => {
		const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
		const open = Boolean(anchorEl);

		const handleClick = (event: any) => {
			setAnchorEl(event.currentTarget);
		};

		const handleClose = () => {
			setAnchorEl(null);
		};


		const handleDelete = async () => {
			const cancelConfirmed = await dialogs.confirm(
				`Are you sure you want to Update Post "${props.data['title']}"?`,
				{
					title: "Delete Update Post",
					cancelText: "Cancel",
					okText: "Confirm",
				}
			);
			if (cancelConfirmed) {
				deleteUpdatePostMutation.mutate([props.data["id"]]);
			}
			handleClose();
		};

		return (
			<>
				<IconButton
					aria-label="more"
					aria-controls={open ? 'long-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-haspopup="true"
					onClick={handleClick}
				>
					<MoreVert/>
				</IconButton>
				<Menu
					id="long-menu"
					MenuListProps={{
						'aria-labelledby': 'long-button',
					}}
					anchorEl={anchorEl}
					open={open}
					onClose={handleClose}
				>					
					<MenuItem onClick={() => {
                        setEditingPostData(props.data);
                        setOpenAddUpdatePost(true);
                    }}>

						<Edit color={"warning"} sx={{mr: 1}}/>
						<Typography variant="subtitle2">Edit Update</Typography>
					</MenuItem>
					{<MenuItem onClick={handleDelete} >
						<Delete color="error" sx={{ mr: 1 }}/>
						<Typography variant="subtitle2">Delete Update</Typography>
					</MenuItem>}
				</Menu>
			</>
		);
	}

    const columnDefs: ColDef[] = [                  
            {field: "title", headerName: "Title"},
            {
                field: "is_draft",
                headerName: "Status",
                cellRenderer: (params: any) => {
                    const isDraft = params.value;
                    return (
                        <Chip
                            label={isDraft ? "Draft" : "Published"}
                            color={isDraft ? "warning" : "success"}
                            size="small"
                            variant={isDraft ? "outlined" : "filled"}
                        />
                    );
                },
            },
            {field: "created_at", headerName: "Created On", valueFormatter: timestampFormatter},        
            {headerName: "Actions", cellRenderer: ActionsButton, sortable: false, resizable: false},
        ]

    if (pageDataQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (pageDataQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack direction={"column"} spacing={2} sx={{pb: 4, height: "100%"}}>
				<Typography fontWeight={"bold"} variant={"h5"}>Manage Update Section</Typography>
				<Divider/>
				<Stack direction={"column"} spacing={2} sx={{height: "100%"}}>					
					<Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
						<Stack direction={"row"} spacing={2}>
							<Button variant={"contained"}
											size={"small"}
											startIcon={<AddCircleOutline/>}
											disabled={false}
											onClick={() => {
                                                setOpenAddUpdatePost(true)
                                                setEditingPostData(null);
                                            }}>
								Create Update Post
							</Button>
							<Button color={"error"}
											variant={"contained"}
											size={"small"}
											startIcon={<Delete/>}
											disabled={selectedRows.length === 0}
                                            onClick={() => {
                                                const ids = selectedRows.map(row => row.id);
                                                handleDeleteSelected(ids);
                                            }}>
								Delete Selected
							</Button>
						</Stack>
					</Box>
					{/* Table */}
					<Box sx={{height: "100%"}}>
                        <PaginatedServerSideDataTable 
                            columns={columnDefs} 
                            paginationData={pageData} 
                            loading={pageDataQuery.isLoading || pageDataQuery.isFetching}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onSearchChange={handleSearchChange}
                            onSelectionChange={(rows) => {setSelectedRows(rows); }} 
                            rowSelection={{ mode: "multiRow" }} 
                            noRowsText={"No Update Posts Available"}
                        />
                    </Box>
				</Stack>		
                <AddNewAndEditUpdatePost open={openAddUpdatePost}
                        campaignCustomStartDatetime={campaignCustomStartDatetime}
                        editingData={editingPostData}
                        onClose={() => setOpenAddUpdatePost(false)}
                        onSuccess={() => {
                            setOpenAddUpdatePost(false);
                            pageDataQuery.refetch().then();
                        }}/>		
			</Stack>
		)
	} else {
		return <></>
	}
}


function AddNewAndEditUpdatePost(props: {
    open: boolean,
    editingData: any | null,
    campaignCustomStartDatetime: DateTime | null,
    onClose: () => void,
    onSuccess: () => void,
}) {
    const { enqueueSnackbar } = useSnackbar();
    const quillRef = useRef<ReactQuill | null>(null);

    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [datetime, setDatetime] = useState<DateTime | null>(props.campaignCustomStartDatetime);

    const handleImageUpload = useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/png, image/jpeg, image/jpg, image/webp, image/gif');
        input.click();

        input.onchange = () => {
            const file = input.files?.[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = () => {
                const base64Url = reader.result as string;
                const editor = quillRef.current?.getEditor();
                if (editor) {
                    const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
                    editor.insertEmbed(range.index, 'image', base64Url);
                    editor.setSelection(range.index + 1, 0);
                    setDescription(editor.root.innerHTML);
                }
            };
            reader.readAsDataURL(file);
        };
    }, []);
    
    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                ['link', 'image'],
                ['clean']
            ],
            handlers: {
                image: handleImageUpload
            }
        },
        imageResize: {
            parchment: Quill.import('parchment'),
            modules: ['Resize', 'DisplaySize']
        },
        clipboard: {
            matchVisual: false,
        },
    }), [handleImageUpload]);

    useEffect(() => {
        if (props.editingData) {
            setTitle(props.editingData.title || '');
            setDescription(props.editingData.description || '');            
            setDatetime(DateTime.fromISO(props.editingData.created_at));
        } else {
            setTitle('');
            setDescription('');
            setDatetime(null);
        }
    }, [props.editingData]);
    
    // Updated mutation to include `is_draft`
    const postMutation = useMutation({
        mutationKey: ["addOrEditUpdatePost"],
        mutationFn: async ({ id, title, description, date, is_draft }: { id?: number; title: string; description: string; date: string; is_draft: boolean }) => {
            const url = id ? "/admin/admin-edit-update-post/" : "/admin/admin-add-update-post/";
            const payload = id
                ? { id, title, description, date, is_draft }
                : { title, description, date, is_draft };

            return await authenticateAndPostData(url, payload);
        },
        gcTime: 0,
        retry: retryFn,
        onSuccess: (_data, variables) => {
            setTitle('');
            setDescription('');
            setDatetime(null);
            props.onSuccess();
            const actionText = variables.is_draft 
                ? "saved as draft" 
                : (variables.id ? "updated" : "published");
            enqueueSnackbar(`Successfully ${actionText} the update post.`, { variant: "success" });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, { variant: 'error' });
        },
    });

    function handleSubmit(isDraft: boolean) {    
        if (!title.trim() || !description.trim() || !datetime) {
            enqueueSnackbar("Please fill in all fields.", { variant: "warning" });
            return;
        }
                    
        const sanitizedDescription = description.replace(
            /href="((?!http|https|mailto|tel)[^"]+)"/g, 
            'href="https://$1"'
        );

        postMutation.mutate({
            id: props.editingData?.id,
            title,
            description: sanitizedDescription,
            date: datetime.toISO({ suppressMilliseconds: true, suppressSeconds: true }) ?? "",
            is_draft: isDraft,
        });
    }

    const isSubmitDisabled = postMutation.isPending || !title.trim() || !description.trim() || !datetime;

    return (
        <>
        <GlobalStyles styles={{          
            '.quill-wrapper .ql-editor img': {                
                margin: '8px',
                border: '1px solid #e0e0e0',                
                cursor: 'pointer !important',                
                minWidth: '80px !important', 
                minHeight: '40px !important',
                height: 'auto !important', 
                objectFit: 'contain',
            },            
            '.quill-wrapper .ql-editor': {
                minHeight: '180px',
            },           
            '.ql-image-resize-module img': {
                minWidth: '80px !important',
                minHeight: '40px !important',
                height: 'auto !important',
                cursor: 'pointer !important',
            }
        }} />
        <Dialog open={props.open} onClose={props.onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {props.editingData ? "Edit Update Post" : "Add Update Post"}
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 1 }}>Title:</DialogContentText>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Enter title..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />

                <DialogContentText sx={{ mb: 1, mt: 2 }}>Description (Rich Text):</DialogContentText>                    
                <Box className="quill-wrapper" sx={{ 
                    mb: 2, 
                    '& .ql-container': { borderBottomLeftRadius: 4, borderBottomRightRadius: 4 },
                    '& .ql-toolbar': { borderTopLeftRadius: 4, borderTopRightRadius: 4 }
                }}>
                    <ReactQuill ref={quillRef} theme="snow" value={description} onChange={setDescription} modules={modules} />
                </Box>

                <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <Typography variant="body1" sx={{ mt: 3, fontWeight: "bold" }}>
                        Timezone: {DateTime.local().zoneName}
                    </Typography>
                    <DateTimePicker
                        value={datetime}
                        onChange={(val) => setDatetime(val)}
                        timezone={DateTime.local().zoneName}
                        sx={{ mt: 2 }}
                    />
                </LocalizationProvider>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
                <Button onClick={props.onClose} disabled={postMutation.isPending}>
                    Cancel
                </Button>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => handleSubmit(true)}
                        disabled={isSubmitDisabled}
                    >
                        Save as Draft
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => handleSubmit(false)}
                        disabled={isSubmitDisabled}
                    >
                        {props.editingData ? "Publish Post" : "Add Update Post"}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
        </>
    );
}
